export type EventType = "incident" | "maintenance" | "info";
export type LanguagePolicy = "bilingual";
export type MessagePhase =
  | "announcement"
  | "reminder"
  | "start"
  | "update"
  | "close"
  | "adhoc";

export type User = {
  id: string;
  principal: string;
  display_name: string;
  email: string;
  role: string;
};

export type ApiKey = {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  created_at: string;
  expires_at: string | null;
  revoked_at: string | null;
};

export async function listApiKeys(): Promise<ApiKey[]> {
  return (await request<{ items: ApiKey[] }>("/api-keys")).items;
}

export async function listApiKeyScopes(): Promise<string[]> {
  return (await request<{ items: string[] }>("/api-keys/scopes")).items;
}

export function createApiKey(body: { name: string; scopes: string[]; expires_at: string | null }) {
  return request<ApiKey & { key: string }>("/api-keys", {
    method: "POST", body: JSON.stringify(body),
  });
}

export function revokeApiKey(id: string): Promise<void> {
  return request(`/api-keys/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export type ManagedUser = {
  id: string;
  principal: string;
  display_name: string;
  email: string;
  role: string;
  active: boolean;
};

export type ManagedUserCreate = {
  principal: string;
  display_name: string;
  email: string;
  role: string;
  active: boolean;
  password: string | null;
};

export type ManagedUserUpdate = Partial<Omit<ManagedUserCreate, "principal">>;

export async function listUsers(): Promise<ManagedUser[]> {
  return (await request<{ items: ManagedUser[] }>("/users")).items;
}

export function createUser(body: ManagedUserCreate): Promise<ManagedUser> {
  return request<ManagedUser>("/users", { method: "POST", body: JSON.stringify(body) });
}

export function updateUser(id: string, body: ManagedUserUpdate): Promise<ManagedUser> {
  return request<ManagedUser>(`/users/${encodeURIComponent(id)}`, {
    method: "PATCH", body: JSON.stringify(body),
  });
}

export type AudienceFilter = {
  source?: string | null;
  rules?: string[];
  /** Read-only fallback: old rows may carry only `shorts`. Never sent. */
  shorts?: string[];
};

export type DeliveryProgress = {
  submitted: number;
  total: number;
  unknown: number;
  failed: number;
  queued: number;
  /** Angenommene Kopien, zu denen ein Rückläufer kam. */
  bounced?: number;
};

/** Was aus den Kopien einer Mail wurde (Archiv). Abgebrochene zählen nicht mit. */
export type DeliveryStats = {
  total: number;
  /** SMTP-Annahme, Rückläufer eingeschlossen. */
  submitted: number;
  /** Angenommen und kein Rückläufer. */
  delivered: number;
  bounced: number;
  rejected: number;
  failed: number;
  unknown: number;
  queued: number;
  bounce_categories: Record<string, number>;
};

export type Event = {
  id: string;
  type: EventType;
  status: string;
  title: string;
  placeholders: Record<string, string>;
  placeholders_en?: Record<string, string>;
  language_policy: string;
  sender_id: string | null;
  thread_topic: string;
  audience_filter?: AudienceFilter;
  audience_snapshot_id?: string | null;
  audience_label?: string | null;
  resolution?: string | null;
  sent_count?: number;
  last_phase?: string | null;
  delivery_progress?: DeliveryProgress;
  created_at: string;
  updated_at: string;
  messages?: Message[];
};

export type AuditEntry = {
  id: string;
  at: string;
  actor_id: string;
  actor_display_name: string | null;
  action: string;
  subject_type: string;
  subject_id: string;
  detail: Record<string, unknown> | null;
};

export type StatsWindow = "24h" | "7d" | "30d" | "all";

export type StatsPoint = {
  at: string;
  submitted: number;
  failed: number;
  unknown: number;
  bounced?: number;
};

export type StatsSnapshot = {
  window: StatsWindow;
  generated_at: string;
  messages: Record<string, number>;
  deliveries: Record<string, number>;
  /** accepted, bounced, delivered, cat_<Kategorie>, delayed, auto_reply, unmatched */
  bounces?: Record<string, number>;
  series: StatsPoint[];
  events: {
    id: string;
    title: string;
    type: EventType;
    status: string;
    sent_count?: number;
    delivery_progress: DeliveryProgress;
  }[];
};

export type SentMessage = {
  id: string;
  phase: MessagePhase;
  state: string;
  /** Gerenderter deutscher Betreff, Platzhalter gefüllt. */
  subject: string;
  sent_at: string;
  /** Rückläufer zählen bei der Mail, egal wann sie eintreffen. */
  stats: DeliveryStats;
};

export type SentOverview = {
  window: StatsWindow;
  generated_at: string;
  mails: number;
  stats: DeliveryStats;
  /** queued, deferred_<Grund>, submitting, unknown – unabhängig vom Zeitraum. */
  open: Record<string, number>;
  events: {
    id: string;
    title: string;
    type: EventType;
    status: string;
    stats: DeliveryStats;
    messages: SentMessage[];
  }[];
  events_total: number;
};

export type Recipient = {
  id: string;
  address: string;
  domain: string;
  list_id_short: string | null;
  language: string;
  state: string;
  defer_reason: string | null;
  attempts: number;
  next_attempt_at: string | null;
  last_error: string | null;
  smtp_response: string | null;
  submitted_at: string | null;
  finished_at: string | null;
  bounced_at: string | null;
  bounce_category: string | null;
  bounce_status: string | null;
  bounce_diagnostic: string | null;
};

export type SentMessageDetail = {
  message: SentMessage;
  event_id: string;
  event_title: string;
  event_type: EventType;
  recipients: Recipient[];
};

export type Message = {
  id: string;
  event_id: string;
  phase: MessagePhase;
  state: string;
  subject_de: string;
  subject_en: string;
  html_de: string;
  html_en: string;
  text_de: string;
  text_en: string;
  created_from: string;
  origin: string;
  scheduled_for?: string | null;
  audience?: string | null;
  audience_title?: string | null;
  recipient_count?: number;
  /** Nur in der Archiv-Liste. */
  delivery_stats?: DeliveryStats | null;
  created_at: string;
  updated_at: string;
  deliveries?: Delivery[];
};

export type Delivery = {
  id: string;
  message_id?: string;
  address: string;
  domain?: string;
  rfc_message_id: string | null;
  state: string;
  defer_reason?: string | null;
  attempts?: number;
  next_attempt_at?: string | null;
  last_error?: string | null;
  smtp_response: string | null;
  language?: string;
  submitted_at?: string | null;
  queued_at?: string | null;
  created_at?: string;
  bounced_at?: string | null;
  bounce_category?: string | null;
  test?: boolean;
  /** Archive copy to the sender's copy_to address, not a recipient. */
  archive_copy?: boolean;
};

export type QueueSnapshot = {
  counts: Record<string, number>;
  items: (Delivery & { event_id: string; event_title: string; message_phase: string })[];
};

export type Template = {
  id: string;
  title: string;
  event_type: EventType | null;
  phase: MessagePhase;
  subject_de: string;
  subject_en: string;
  html_de: string;
  html_en: string;
  text_de: string;
  text_en: string;
  built_in: boolean;
};

export type ArchiveEventItem = {
  event: Event;
  messages: Message[];
  last_sent_at: string;
  first_sent_at: string;
  recipient_count: number;
  delivery_stats?: DeliveryStats;
};

export type Sender = {
  id: string;
  display_name: string;
  header_from: string;
  envelope_from: string;
  reply_to: string | null;
  text_signature: string;
  dkim_domain: string;
  /** Fingerprint; gesetzt = jede Mail geht PGP/MIME-signiert raus. */
  pgp_sign_key: string | null;
  /** Gesetzt = jeder echte Versand geht zusätzlich einmal an diese Adresse. */
  copy_to: string | null;
};

export type PgpKey = {
  fingerprint: string;
  uids: string[];
  emails: string[];
  created: string | null;
  expires: string | null;
  status: "good" | "expired" | "revoked";
  has_secret: boolean;
};

export type PgpKeyring = {
  /** false: EDICTA_PGP_GNUPGHOME ist nicht gesetzt. */
  configured: boolean;
  error: string | null;
  keys: PgpKey[];
};

export type Preview = {
  subject: string;
  html: string;
  text: string;
  headers: {
    From: string;
    "List-Id": string;
    "Thread-Topic": string;
    "Auto-Submitted": string;
    Precedence: string;
    [key: string]: string;
  };
};

export type EventCreate = {
  type: EventType;
  title: string;
  placeholders: Record<string, string>;
  placeholders_en?: Record<string, string>;
  language_policy: LanguagePolicy;
  sender_id: string | null;
  audience_filter?: AudienceFilter;
};

export type EventPatch = Partial<EventCreate>;

export type MessageCreate = {
  phase?: MessagePhase;
  template_id?: string;
  subject_de: string;
  subject_en: string;
  html_de: string;
  html_en: string;
  text_de: string;
  text_en: string;
};

export type MessagePatch = Partial<MessageCreate>;

export type SenderWrite = {
  display_name: string;
  header_from: string;
  envelope_from: string;
  reply_to: string | null;
  text_signature: string;
  dkim_domain: string;
  pgp_sign_key: string | null;
  copy_to: string | null;
};

export type SendRequest = {
  address?: string | null;
  audience?: "demo" | "demo-gmail" | "filter" | null;
  list_id_short: string | null;
  scheduled_for?: string | null;
  /** Test mail: the mail stays a draft. */
  test?: boolean;
};

export type ImportRule = {
  id: string;
  slug: string;
  title: string;
  kind: string;
  id_regexp: string | null;
  sort_order: number;
};

export type SourceFilter = {
  column: string;
  equals: string | number | boolean;
};

export type SourceConnector = {
  driver: string;
  host: string;
  port: number;
  database: string;
  user: string;
  password_set: boolean;
  table: string;
  id_column: string;
  email_column: string;
  filters: SourceFilter[];
};

export type DirectoryStatus = {
  current_generation: number;
  synced_at: string | null;
  age_seconds: number | null;
  replica_count: number;
  stale: boolean;
  filters_drift: boolean;
  last_sync_status: string;
  last_sync_error: string | null;
};

export type ImportSource = {
  id: string;
  slug: string;
  title: string;
  kind: string;
  hint: string;
  enabled: boolean;
  default: boolean;
  config: SourceConnector;
  rules: ImportRule[];
  directory?: DirectoryStatus;
};

export type AudiencePreview = {
  filter: AudienceFilter;
  label: string;
  recipient_count: number;
  without_address: number;
  duplicate_users: number;
  bottleneck: string | null;
  bottleneck_n: number;
  domains: { domain: string; n: number }[];
  shorts: { short: string | null; n: number }[];
  source: string;
  synced_at?: string | null;
  age_seconds?: number | null;
  replica_count?: number;
  stale?: boolean;
  filters_drift?: boolean;
  last_sync_status?: string;
  /** Adressen der Vorschau mit Rückläufer-Vermerk; bleiben im Empfängerkreis. */
  invalid_count?: number;
  invalid_addresses?: { address: string; last_bounce_at: string | null; last_category: string | null }[];
};

export type DemoAudience = {
  id: "demo" | "demo-gmail";
  title: string;
  hint: string;
  count: number;
  bottleneck: string;
  bottleneck_n: number;
  domains: { domain: string; n: number }[];
  recipients: { address: string; list_id_short: string | null; domain: string }[];
};

const API = "/api/v1";

export class ApiError extends Error {
  status: number;
  body: unknown;
  code?: string;

  constructor(status: number, message: string, body?: unknown, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    this.code = code;
  }
}

let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null) {
  onUnauthorized = handler;
}

function detailCode(data: unknown): string | undefined {
  if (!data || typeof data !== "object") return undefined;
  const detail = (data as { detail?: unknown }).detail;
  if (detail && typeof detail === "object" && "code" in detail) {
    const code = (detail as { code?: unknown }).code;
    if (typeof code === "string" && code) return code;
  }
  return undefined;
}

function formatDetail(data: unknown, fallback: string): string {
  if (!data || typeof data !== "object") {
    return fallback;
  }
  const detail = (data as { detail?: unknown; message?: unknown }).detail;
  if (typeof detail === "string" && detail) {
    return detail;
  }
  if (detail && typeof detail === "object" && "message" in detail) {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === "string" && message) {
      return message;
    }
  }
  if (Array.isArray(detail)) {
    const parts = detail.map((item) => {
      if (item && typeof item === "object" && "msg" in item) {
        return String((item as { msg: unknown }).msg);
      }
      return JSON.stringify(item);
    });
    if (parts.length) {
      return parts.join("; ");
    }
  }
  const message = (data as { message?: unknown }).message;
  if (typeof message === "string" && message) {
    return message;
  }
  return fallback;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const res = await fetch(`${API}${path}`, {
    ...init,
    credentials: "include",
    headers,
  });

  if (res.status === 401) {
    if (!path.startsWith("/auth/login")) {
      onUnauthorized?.();
    }
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = undefined;
    }
    throw new ApiError(401, formatDetail(body, "Nicht angemeldet"), body, detailCode(body));
  }

  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = undefined;
    }
    throw new ApiError(
      res.status,
      formatDetail(body, res.statusText || "Anfrage fehlgeschlagen"),
      body,
      detailCode(body),
    );
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  if (!text) {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}

export function login(principal: string, password: string) {
  return request<User | Record<string, never>>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ principal, password }),
  });
}

export function logout() {
  return request<void>("/auth/logout", { method: "POST" });
}

export function getMe() {
  return request<User>("/auth/me");
}

export function getAuthMethods() {
  return request<{ kerberos: boolean }>("/auth/methods");
}

export function kerberosLoginUrl(next?: string): string {
  const params = new URLSearchParams();
  if (next) params.set("next", next);
  const qs = params.toString();
  return `${API}/auth/kerberos${qs ? `?${qs}` : ""}`;
}

export async function listEvents(): Promise<Event[]> {
  const data = await request<{ items: Event[] }>("/events");
  return data.items ?? [];
}

export function createEvent(body: EventCreate) {
  return request<Event>("/events", { method: "POST", body: JSON.stringify(body) });
}

export async function getEvent(id: string): Promise<Event> {
  return request<Event>(`/events/${id}`);
}

export function patchEvent(id: string, body: EventPatch) {
  return request<Event>(`/events/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}

export function closeEvent(id: string) {
  return request<Event>(`/events/${id}/close`, { method: "POST" });
}

export function cancelEvent(id: string) {
  return request<Event>(`/events/${id}/cancel`, { method: "POST" });
}

export function discardMessage(eventId: string, messageId: string) {
  return request<Event>(`/events/${eventId}/messages/${messageId}`, { method: "DELETE" });
}

export function createMessage(eventId: string, body: MessageCreate) {
  return request<Message>(`/events/${eventId}/messages`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function getMessage(id: string) {
  return request<Message>(`/messages/${id}`);
}

export function patchMessage(id: string, body: MessagePatch) {
  return request<Message>(`/messages/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}

export function previewMessage(id: string) {
  return request<Preview>(`/messages/${id}/preview`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export type LlmStatus = { available: boolean; status_feed?: boolean };

export function llmStatus() {
  return request<LlmStatus>("/llm/status");
}

export type LlmRefineRequest = {
  html: string;
  language: "de" | "en";
  event_id?: string;
};

export type LlmRefineResponse = { html: string };

export function llmRefine(body: LlmRefineRequest) {
  return request<LlmRefineResponse>("/llm/refine", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export type LlmTranslateRequest = {
  subject_de: string;
  html_de: string;
  placeholders: Record<string, string>;
  event_id?: string;
  /** German text still as in the template: only the placeholder values are translated. */
  values_only?: boolean;
};

export type LlmTranslateResponse = {
  /** null with values_only: the English text stays as it is. */
  subject_en: string | null;
  html_en: string | null;
  placeholders_en: Record<string, string>;
  warnings: string[];
};

export function llmTranslate(body: LlmTranslateRequest) {
  return request<LlmTranslateResponse>("/llm/translate", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export type IncidentDraft = {
  incident: boolean;
  overall: string;
  checked_at: string;
  /** Operator view: layers/buildings with hostnames and raw problems. */
  findings: string[];
  impacts: string[];
  buildings: string[];
  maintenances: string[];
  /** When the outage began (status page, local time), if it says. */
  since?: string | null;
  /** Values from the status: building, and date/start ("10:10 Uhr", rounded down) when known. */
  placeholders?: Record<string, string>;
  /** Null when the affected buildings match no rule — never "everyone" by accident. */
  audience: { source: string | null; shorts: string[]; all: boolean; unmatched: string[] } | null;
  draft: { subject_de: string; html_de: string; placeholders: Record<string, string> } | null;
  draft_error: string | null;
};

export function llmIncidentDraft(source?: string | null) {
  return request<IncidentDraft>("/llm/incident-draft", {
    method: "POST",
    body: JSON.stringify({ source: source ?? null }),
  });
}

/** "outage": every host down at once (worst case); "redundancy": pairs only lose their redundancy. */
export type MaintenanceMode = "outage" | "redundancy";

export type DraftAudience = IncidentDraft["audience"];

export type MaintenanceVariant = {
  network_wide: boolean;
  buildings: string[];
  impacts: string[];
  /** false: only hosts outside the status page's layers — the operator has to describe the impact. */
  impact_known: boolean;
};

export type Maintenance = {
  id: string;
  name: string;
  description: string;
  is_active: boolean;
  /** Next (or running) window, local time of the status page. */
  start: string | null;
  end: string | null;
  window_count: number;
  /** Empty when customers notice nothing either way. */
  variants: Partial<Record<MaintenanceMode, MaintenanceVariant>>;
  default_mode: MaintenanceMode | null;
  findings: string[];
};

export type MaintenanceList = { items: Maintenance[]; checked_at: string; days: number; message: string | null };

export type MaintenanceDraft = {
  maintenance: Maintenance;
  mode: MaintenanceMode | null;
  /** building/date/start/end — also without a draft, for a template. */
  placeholders: Record<string, string>;
  audience: DraftAudience;
  draft: IncidentDraft["draft"];
  draft_error: string | null;
};

export function llmMaintenances() {
  return request<MaintenanceList>("/llm/maintenances");
}

/** `impact`: what residents notice, in the operator's words — replaces the status page's impacts. */
export function llmMaintenanceDraft(maintenanceId: string, mode?: MaintenanceMode | null, impact?: string | null) {
  return request<MaintenanceDraft>("/llm/maintenance-draft", {
    method: "POST",
    body: JSON.stringify({ maintenance_id: maintenanceId, mode: mode ?? null, impact: impact || null }),
  });
}

export async function listSources(): Promise<ImportSource[]> {
  const data = await request<{ items: ImportSource[] }>("/sources");
  return data.items ?? [];
}

export function patchSource(
  id: string,
  body: {
    title?: string;
    enabled?: boolean;
    config?: Partial<Omit<SourceConnector, "password_set">> & {
      password?: string;
      filters?: SourceFilter[];
    };
  },
) {
  return request<ImportSource>(`/sources/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}

export function syncSource(id: string, body: { force_empty?: boolean } = {}) {
  return request<ImportSource>(`/sources/${id}/sync`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function testSourceConnector(
  id: string,
  body: Partial<Omit<SourceConnector, "password_set">> & { password?: string },
) {
  return request<{ ok: boolean; detail: string }>(`/sources/${id}/test`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export type DiscoverField = "title" | "pattern";

type DiscoverValue = { current: string | null; proposed: string | null; changed: boolean };

/** One row of the discovery comparison: a new rule or an existing one that differs. */
export type DiscoverProposal = {
  slug: string;
  status: "new" | "changed";
  title: DiscoverValue;
  pattern: DiscoverValue;
  /** Title was set by hand (neither slug nor the upstream name). */
  title_manual: boolean;
};

export function previewDiscoveredRules(id: string) {
  return request<{ items: DiscoverProposal[]; found: number }>(`/sources/${id}/discover/preview`, {
    method: "POST",
    body: "{}",
  });
}

export function applyDiscoveredRules(id: string, items: { slug: string; fields: DiscoverField[] }[]) {
  return request<ImportSource>(`/sources/${id}/discover`, {
    method: "POST",
    body: JSON.stringify({ items }),
  });
}

export function createSourceRule(
  sourceId: string,
  body: { slug: string; title?: string; id_regexp: string },
) {
  return request<ImportRule>(`/sources/${sourceId}/rules`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function patchSourceRule(
  sourceId: string,
  ruleId: string,
  body: { slug?: string; title?: string; id_regexp?: string | null },
) {
  return request<ImportRule>(`/sources/${sourceId}/rules/${ruleId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function deleteSourceRule(sourceId: string, ruleId: string) {
  return request<void>(`/sources/${sourceId}/rules/${ruleId}`, { method: "DELETE" });
}

export function previewAudience(eventId: string, body: AudienceFilter) {
  return request<AudiencePreview>(`/events/${eventId}/audience/preview`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function listDemoAudiences(): Promise<DemoAudience[]> {
  const data = await request<{ items: DemoAudience[] }>("/demo-audiences");
  return data.items ?? [];
}

export function sendMessage(id: string, body: SendRequest) {
  return request<Message>(`/messages/${id}/send`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function cancelScheduledSend(id: string) {
  return request<Message>(`/messages/${id}/cancel-schedule`, { method: "POST" });
}

export function getQueue() {
  return request<QueueSnapshot>("/queue");
}

export function getStats(window: StatsWindow = "24h") {
  return request<StatsSnapshot>(`/stats?window=${window}`);
}

export function getSentOverview(window: StatsWindow = "7d") {
  return request<SentOverview>(`/stats/sent?window=${window}`);
}

export function getMessageRecipients(messageId: string) {
  return request<SentMessageDetail>(`/messages/${messageId}/recipients`);
}

export async function listAuditEntries(filters: {
  subject_type?: string;
  action?: string;
  limit?: number;
} = {}): Promise<AuditEntry[]> {
  const params = new URLSearchParams();
  if (filters.subject_type) params.set("subject_type", filters.subject_type);
  if (filters.action) params.set("action", filters.action);
  params.set("limit", String(filters.limit ?? 200));
  const data = await request<{ items: AuditEntry[] }>(`/audit?${params.toString()}`);
  return data.items ?? [];
}

export function retryFailed(states: ("failed" | "rejected")[], messageId?: string) {
  return request<{ count: number }>("/queue/retry-failed", {
    method: "POST",
    body: JSON.stringify({ states, message_id: messageId ?? null }),
  });
}

export function deliveryAction(id: string, action: "retry" | "ack" | "cancel") {
  return request<Delivery>(`/deliveries/${id}/action`, {
    method: "POST",
    body: JSON.stringify({ action }),
  });
}

export async function listSenders(): Promise<Sender[]> {
  const data = await request<{ items: Sender[] }>("/senders");
  return data.items ?? [];
}

export function createSender(body: SenderWrite) {
  return request<Sender>("/senders", { method: "POST", body: JSON.stringify(body) });
}

export function patchSender(id: string, body: Partial<SenderWrite>) {
  return request<Sender>(`/senders/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}

export function listPgpKeys() {
  return request<PgpKeyring>("/senders/pgp-keys");
}

/** ASCII-armored öffentlicher Schlüssel des Absenders (kein JSON). */
export async function getSenderPublicKey(id: string): Promise<string> {
  const res = await fetch(`${API}/senders/${id}/pgp-public-key`, { credentials: "include" });
  if (!res.ok) {
    throw new ApiError(res.status, "Öffentlicher Schlüssel nicht verfügbar.", undefined);
  }
  return res.text();
}

export async function listTemplates(): Promise<Template[]> {
  const data = await request<{ items: Template[] }>("/templates");
  return data.items ?? [];
}

/** New event with a draft copy of a sent or archived mail ("Als Entwurf"). */
export function draftFromMessage(messageId: string) {
  return request<Message>(`/messages/${messageId}/draft`, { method: "POST" });
}

export function saveMessageAsTemplate(messageId: string, title: string) {
  return request<Template>("/templates/from-message", {
    method: "POST",
    body: JSON.stringify({ message_id: messageId, title }),
  });
}

export type LegacyOriginal = {
  message_id: string;
  subject: string;
  sent_at: string | null;
  lists: string[];
  sender: string | null;
  text: string;
  filename: string | null;
};

export function getOriginal(id: string) {
  return request<LegacyOriginal>(`/messages/${id}/original`);
}

export function blankMessage(phase: MessagePhase): MessageCreate {
  return {
    phase,
    subject_de: "",
    subject_en: "",
    html_de: "",
    html_en: "",
    text_de: "",
    text_en: "",
  };
}

export async function listArchiveEvents(
  opts: {
    limit?: number;
    offset?: number;
    q?: string;
    type?: EventType;
    year?: number;
    source?: "legacy" | "edicta";
  } = {},
): Promise<{ items: ArchiveEventItem[]; total: number }> {
  const params = new URLSearchParams();
  if (opts.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts.offset !== undefined) params.set("offset", String(opts.offset));
  if (opts.q) params.set("q", opts.q);
  if (opts.type) params.set("type", opts.type);
  if (opts.year !== undefined) params.set("year", String(opts.year));
  if (opts.source) params.set("source", opts.source);
  const query = params.toString();
  const data = await request<{ items: ArchiveEventItem[]; total?: number }>(
    `/archive/events${query ? `?${query}` : ""}`,
  );
  const items = data.items ?? [];
  return { items, total: data.total ?? items.length };
}

export type AddressStatus = {
  address: string;
  bounces: number;
  consecutive: number;
  invalid: boolean;
  first_bounce_at: string | null;
  last_bounce_at: string | null;
  last_category: string | null;
  last_status_code: string | null;
  last_diagnostic: string | null;
  cleared_at: string | null;
};

export type BounceReport = {
  id: string;
  received_at: string;
  delivery_id: string | null;
  message_id: string | null;
  event_id: string | null;
  event_title: string | null;
  address: string | null;
  kind: string;
  category: string | null;
  status_code: string | null;
  diagnostic: string | null;
  matched_by: string;
  subject: string;
  from_header: string;
  raw: string | null;
};

export type BounceStatus = {
  configured: boolean;
  verp: boolean;
  poll_seconds: number;
  last_run_at: string | null;
  last_ok_at: string | null;
  last_error: string | null;
  last_fetched: number;
};

export function getBounceStatus() {
  return request<BounceStatus>("/bounces/status");
}

export function listBounceAddresses(filters: {
  invalid_only?: boolean;
  q?: string;
  limit?: number;
  offset?: number;
} = {}) {
  const params = new URLSearchParams();
  if (filters.invalid_only) params.set("invalid_only", "true");
  if (filters.q) params.set("q", filters.q);
  params.set("limit", String(filters.limit ?? 100));
  params.set("offset", String(filters.offset ?? 0));
  return request<{ items: AddressStatus[]; total: number }>(`/bounces/addresses?${params.toString()}`);
}

export function clearBounceAddress(address: string) {
  return request<AddressStatus>(`/bounces/addresses/${encodeURIComponent(address)}/clear`, {
    method: "POST",
  });
}

export function listBounceReports(filters: {
  kind?: string;
  unmatched?: boolean;
  q?: string;
  eventId?: string;
  messageId?: string;
  limit?: number;
  offset?: number;
} = {}) {
  const params = new URLSearchParams();
  if (filters.kind) params.set("kind", filters.kind);
  if (filters.unmatched) params.set("unmatched", "true");
  if (filters.q) params.set("q", filters.q);
  if (filters.eventId) params.set("event_id", filters.eventId);
  if (filters.messageId) params.set("message_id", filters.messageId);
  params.set("limit", String(filters.limit ?? 100));
  params.set("offset", String(filters.offset ?? 0));
  return request<{ items: BounceReport[]; total: number }>(`/bounces/reports?${params.toString()}`);
}

export function getBounceReport(id: string) {
  return request<BounceReport>(`/bounces/reports/${id}`);
}
